import { useMemo } from 'react';
import { useLandingPageTranslation } from './useLandingPageTranslation';

import RoundButton from '@basic/RoundButton';
import LoaderIcon from '@components/icons/LoaderIcon';
import { DEFAULT_LANDING_PAGE_ACCENT_COLOR } from '@consts';
import { GoalEnrollReason } from '@models/goals';
// PayU payment button temporarily hidden.
// import { PaymentProvider } from '@models/payments';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { Status, usePaymentAndOrder } from '../../payments/PaymentAndOrderContext';
import { LANDING_CTA_GLOW_CLASS, accentGlowStyle } from './accentGlow';

export default function GoalLandingPageMainCTAButton() {
  const { t } = useLandingPageTranslation();
  const {
    state,
    isSubscribed,
    isSoldOut,
    isFree,
    canEnroll,
    isLoading,
    isAuthor,
    handleMainAction
  } = usePaymentAndOrder();

  const accentColor = state.goal?.landingPage?.accentColor || DEFAULT_LANDING_PAGE_ACCENT_COLOR;
  const showGlow = !isLoading && !isAuthor;
  // PayU payment button temporarily hidden — the provider choice only makes sense for a paid,
  // not-yet-owned goal (the "buyNow" state); every other state has a single meaningful action.
  // const showPayU =
  //   !isLoading &&
  //   !isAuthor &&
  //   !isSubscribed &&
  //   !isSoldOut &&
  //   !isFree &&
  //   !canEnroll &&
  //   state.status !== Status.PENDING_ACTIVATION;
  const text = useMemo(() => {
    // processing state
    if (state.status === Status.PENDING_ACTIVATION) {
      return isFree || canEnroll
        ? t('domains.payments.CTAs.processingFree')
        : t('domains.payments.CTAs.processingPayment');
    }

    // highest priority - if subscribed, then we should redirect to the course page
    if (isSubscribed) {
      return t('domains.payments.CTAs.learnNow');
    }

    // second priority - if sold out, then we should redirect to the recommended goals section
    if (isSoldOut) {
      return t('domains.payments.CTAs.soldOut');
    }

    // third priority - if free or paid, then we should redirect to the signup page
    if (isFree) {
      return t('domains.payments.CTAs.free');
    } else if (canEnroll) {
      return t('domains.payments.CTAs.enrollNow');
    } else {
      return t('domains.payments.CTAs.buyNow');
    }
  }, [isSubscribed, isSoldOut, isFree, canEnroll, state.status]);

  const showEnrollReason = useMemo(() => {
    return (
      state.goal?.enrollSpec?.reason === GoalEnrollReason.ACQUIRED_AT_IMPRV ||
      state.goal?.enrollSpec?.reason === GoalEnrollReason.ACQUIRED_AT_GOTOIT ||
      (state.goal?.enrollSpec?.reason === GoalEnrollReason.FREE && !isSoldOut)
    );
  }, [state.goal?.enrollSpec?.reason, isSoldOut]);

  const enrollReasonText = useMemo(() => {
    if (state.goal?.enrollSpec?.reason === GoalEnrollReason.ACQUIRED_AT_IMPRV) {
      return t('domains.payments.CTAs.enrollReasonAcquiredAtImprv');
    }
    if (state.goal?.enrollSpec?.reason === GoalEnrollReason.ACQUIRED_AT_GOTOIT) {
      return t('domains.payments.CTAs.enrollReasonAcquiredAtGoToIt');
    }
    if (state.goal?.enrollSpec?.reason === GoalEnrollReason.FREE) {
      return t('domains.payments.CTAs.enrollReasonFree');
    }
  }, [state.goal?.enrollSpec?.reason]);

  const button = (
    <RoundButton
      type='color'
      size='medium'
      color={isAuthor ? '#8E8E93' : accentColor}
      onClick={handleMainAction}
      disabled={isLoading || isAuthor}
      data-testid='goal-landing-page-main-cta'
      style={showGlow ? accentGlowStyle(accentColor) : undefined}
      className={showGlow ? LANDING_CTA_GLOW_CLASS : undefined}
    >
      <div className='flex flex-row items-center gap-xs justify-center'>
        {isLoading && <LoaderIcon size={20} />}
        {text}
      </div>
    </RoundButton>
  );

  return (
    <div className='w-full flex flex-col gap-xs'>
      {showEnrollReason && (
        <div className={classNames('P4M px-xs', setupForDark('text-black'))}>
          {enrollReasonText}
        </div>
      )}
      {button}
      {/* PayU payment button temporarily hidden:
      {showPayU && (
        <RoundButton
          type='empty'
          size='medium'
          onClick={(e: React.MouseEvent) => handleMainAction(e, PaymentProvider.PAYU)}
          data-testid='goal-landing-page-payu-cta'
        >
          {t('domains.payments.CTAs.buyNowPayU')}
        </RoundButton>
      )}
      */}
      {isAuthor && (
        <div className={classNames('P4R', setupForDark('text-gray-900'))}>
          {t('domains.payments.CTAs.authorCannotEnroll')}
        </div>
      )}
    </div>
  );
}
